/**
 * Storefront v6 — the responsive image layer (v5.67).
 *
 * Every stored image URL is a plain Cloudinary secure_url:
 *   https://res.cloudinary.com/<cloud>/image/upload/[v<version>/]<folder>/<name>.<ext>
 * Cloudinary applies delivery transformations from ONE path segment inserted
 * right after /image/upload/ (f_auto,q_auto,w_800 — with f_auto it picks
 * AVIF/WebP itself), so a srcset is the same asset at several widths. This
 * module is the one place that knows that URL grammar: the Photo primitive
 * asks it for a srcset and never hand-builds one.
 *
 * Anything that is not a Cloudinary upload URL — the Unsplash demo seeds, a
 * data: URL from a Design Studio upload, an arbitrary merchant link, even a
 * Cloudinary /image/fetch/ URL — passes through untouched and gets no
 * srcset: there is nothing honest to resize.
 *
 * Idempotent by construction: a URL that already carries a transformation
 * segment (or a chain of them) has it REPLACED, never doubled, so the same
 * URL can be run through here any number of times.
 */

const UPLOAD = '/image/upload/';

// A Cloudinary URL: a cloudinary.com host (res.cloudinary.com, or a
// <cloud>-res.cloudinary.com private CDN) carrying an /image/upload/ path.
const CLOUDINARY_HOST = /^https?:\/\/(?:[a-z0-9-]+\.)*cloudinary\.com\//i;

// Cloudinary's transformation qualifiers (the `w` of `w_800`, the `dpr` of
// `dpr_2`, a `$var_` variable). A path segment made ONLY of
// `<qualifier>_<value>` parts is a transformation segment; a folder such as
// `meal-items` or `meal_items` is not, and a version `v1789` is not.
const QUALIFIER = /^(?:\$[A-Za-z][A-Za-z0-9]*|a|ac|af|ar|b|bo|br|c|co|cs|d|dl|dn|dpr|du|e|eo|f|fl|fn|fps|g|h|if|ki|l|o|p|pg|q|r|so|sp|t|u|vc|vs|w|x|y|z)_[^,/]+$/;

const isTransformationSegment = (seg) => seg.length > 0 && seg.split(',').every((part) => QUALIFIER.test(part));

const uploadIndex = (url) => {
  if (typeof url !== 'string' || !CLOUDINARY_HOST.test(url)) return -1;
  return url.indexOf(UPLOAD);
};

/** True only for a Cloudinary /image/upload/ URL — the one kind we can resize. */
export const isCloudinary = (url) => uploadIndex(url) !== -1;

// Split a Cloudinary URL into the part up to and including /image/upload/
// and the untransformed tail (version + public id), dropping every leading
// transformation segment. The last segment is the file and is never a
// transformation.
const split = (url) => {
  const i = uploadIndex(url);
  if (i === -1) return null;
  const head = url.slice(0, i + UPLOAD.length);
  const segs = url.slice(i + UPLOAD.length).split('/');
  let k = 0;
  while (k < segs.length - 1 && isTransformationSegment(segs[k])) k += 1;
  return { head, tail: segs.slice(k).join('/') };
};

const segmentFor = ({ width, format = 'auto', quality = 'auto', dpr } = {}) => {
  const parts = [`f_${format || 'auto'}`, `q_${quality || 'auto'}`];
  const w = Number(width);
  if (Number.isFinite(w) && w > 0) parts.push('c_limit', `w_${Math.round(w)}`); // c_limit: never upscale a smaller stored asset
  const d = Number(dpr);
  if (Number.isFinite(d) && d > 0) parts.push(`dpr_${d}`);
  return parts.join(',');
};

/**
 * The same asset delivered at `width` CSS pixels (dpr_2 doubles the device
 * pixels when asked): `f_auto,q_auto,w_N[,dpr_D]` inserted after
 * /image/upload/ exactly once. A non-Cloudinary URL comes back unchanged.
 */
export const cloudinaryVariant = (url, opts) => {
  const parts = split(url);
  if (!parts) return url;
  return `${parts.head}${segmentFor(opts)}/${parts.tail}`;
};

export const DEFAULT_WIDTHS = [400, 640, 960, 1280, 1600];

/** The single-URL `src` that sits beside a srcset — a middle rung. */
export const FALLBACK_WIDTH = 960;

/**
 * A srcset of `url wN` candidates in ascending width, or null when the URL
 * is not a Cloudinary upload (the caller then renders its URL untouched).
 */
export const srcSetFor = (url, widths = DEFAULT_WIDTHS) => {
  if (!isCloudinary(url)) return null;
  const ws = [...new Set((widths || []).map(Number).filter((w) => Number.isFinite(w) && w > 0).map(Math.round))].sort((a, b) => a - b);
  if (!ws.length) return null;
  return ws.map((w) => `${cloudinaryVariant(url, { width: w })} ${w}w`).join(', ');
};

/** How wide each kind of slot is on screen — what `sizes` tells the browser. */
export const SIZES = {
  card: '(min-width:1024px) 25vw, (min-width:768px) 33vw, 50vw',
  hero: '100vw',
  tile: '(min-width:1024px) 33vw, 50vw',
};

export const sizesFor = (kind) => SIZES[kind] || SIZES.card;
